import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Slider,
  Tag,
  Progress,
  Space,
  Toast,
  Input,
} from "@douyinfe/semi-ui";
import { ApiClient } from "../../api/ApiClient";
import { formatAmountZhCn, formatDateTime, displayStar } from "../../utils";
import LeadExactScoringConfig from "./leadExactScoringConfig";

const apiClient = ApiClient.getDefault();

interface LeadExactScoringResult {
  id: number;
  leadId: number;
  totalScore: number;
  scoringDetail: string;
  fieldCount: number;
  updatedAt: string;
  name: string;
  fullMobile: string;
  applyCity: string;
  applyAmount: number;
  star: number;
  assignCount: number;
  house: string;
  vehicle: string;
  providentFund: string;
  socialSecurity: string;
  company: string;
  credit: string;
  income: string;
  workYears: string;
  loanType: string;
}

interface ScoringDetailItem {
  fieldCode: string;
  fieldName: string;
  value: string;
  score: number;
  weight: number;
  baseScore: number;
  empty: boolean;
}

const FIELD_LABELS: Record<string, string> = {
  house: "房产",
  vehicle: "车产",
  providentFund: "公积金",
  socialSecurity: "社保",
  company: "公司",
  credit: "信用",
  income: "收入",
  workYears: "工作年限",
  loanType: "贷款类型",
};

export default function LeadExactScoring() {
  const [data, setData] = useState<LeadExactScoringResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [minScore, setMinScore] = useState(0);
  const [city, setCity] = useState("");
  const [selectedRows, setSelectedRows] = useState<LeadExactScoringResult[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState<LeadExactScoringResult | null>(null);
  const [scoringLoading, setScoringLoading] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
  

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, size: pageSize, minScore };
      if (city) params.city = city;
      const resp = await apiClient.request<{
        current: number;
        total: number;
        records: LeadExactScoringResult[];
      }>("/leadExactScoring/pool", "GET", params);
      if (resp.data) {
        setData(resp.data.records || []);
        setTotal(resp.data.total || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, minScore, city]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleViewDetail = async (record: LeadExactScoringResult) => {
    try {
      const resp = await apiClient.request<LeadExactScoringResult>(
        "/leadExactScoring/detail",
        "GET",
        { leadId: record.leadId }
      );
      if (resp.data) {
        setDetailData(resp.data);
        setShowDetailModal(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleScore = async (leadId: number) => {
    setScoringLoading(true);
    try {
      await apiClient.request("/leadExactScoring/scoreAndSave", "POST", undefined, { leadId });
      Toast.success("评分完成");
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "评分失败");
    } finally {
      setScoringLoading(false);
    }
  };

  const handleBatchScore = async () => {
    if (selectedRows.length === 0) {
      Toast.warning("请先选择客户");
      return;
    }
    setScoringLoading(true);
    try {
      await apiClient.request("/leadExactScoring/scoreBatch", "POST", undefined, {
        leadIds: selectedRows.map((r) => r.leadId),
      });
      Toast.success("批量评分完成");
      setSelectedRows([]);
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "评分失败");
    } finally {
      setScoringLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 60) return "#28a745";
    if (score >= 40) return "#faad14";
    return "#ff4d4f";
  };

  const columns = [
    {
      title: "排名",
      dataIndex: "index",
      width: 55,
      render: (_: any, __: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "客户",
      dataIndex: "name",
      width: 80,
      render: (name: string) => name || "-",
    },
    {
      title: "评分",
      dataIndex: "totalScore",
      width: 130,
      sorter: true,
      render: (score: number) => (
        <Progress
          percent={score}
          size="small"
          style={{ width: 110 }}
          stroke={getScoreColor(score)}
          showInfo
        />
      ),
    },
    {
      title: "字段",
      dataIndex: "fieldCount",
      width: 60,
      render: (v: number) => (
        <Tag size="small" color={v >= 7 ? "green" : v >= 5 ? "blue" : "red"}>
          {v}/9
        </Tag>
      ),
    },
    {
      title: "资质",
      width: 180,
      render: (_: any, record: LeadExactScoringResult) => (
        <Space style={{ gap: 4 }} wrap>
          {record.house && record.house !== "无房" && <Tag size="small" color="blue">房:{record.house}</Tag>}
          {record.vehicle && record.vehicle !== "无车" && <Tag size="small" color="cyan">车:{record.vehicle}</Tag>}
          {record.income && <Tag size="small">收:{record.income}</Tag>}
          {record.credit && <Tag size="small" color={record.credit.includes("逾期") || record.credit.includes("黑户") ? "red" : "green"}>{record.credit}</Tag>}
        </Space>
      ),
    },
    {
      title: "申请金额",
      dataIndex: "applyAmount",
      width: 85,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{formatAmountZhCn(v)}</span>,
    },
    {
      title: "星级",
      dataIndex: "star",
      width: 55,
      render: (v: number) => displayStar(v),
    },
    {
      title: "评分时间",
      dataIndex: "updatedAt",
      width: 120,
      render: (v: string) => v ? formatDateTime(v) : "-",
    },
    {
      title: "操作",
      width: 120,
      render: (_: any, record: LeadExactScoringResult) => (
        <Space>
          <Button size="small" theme="borderless" onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          <Button
            size="small"
            theme="borderless"
            loading={scoringLoading}
            onClick={() => handleScore(record.leadId)}
          >
            重新评分
          </Button>
        </Space>
      ),
    },
  ];

  const renderDetailModal = () => {
    if (!detailData) return null;

    let details: ScoringDetailItem[] = [];
    try {
      details = detailData.scoringDetail ? JSON.parse(detailData.scoringDetail) : [];
    } catch {}

    return (
      <Modal
        title="评分详情"
        visible={showDetailModal}
        onCancel={() => setShowDetailModal(false)}
        footer={
          <Space>
            <Button onClick={() => setShowDetailModal(false)}>关闭</Button>
            <Button
              theme="solid"
              loading={scoringLoading}
              onClick={async () => {
                await handleScore(detailData.leadId);
                // 重新加载详情
                const resp = await apiClient.request<LeadExactScoringResult>(
                  "/leadExactScoring/detail",
                  "GET",
                  { leadId: detailData.leadId }
                );
                if (resp.data) setDetailData(resp.data);
              }}
            >
              重新评分
            </Button>
          </Space>
        }
        width={700}
      >
        {/* 评分概览 */}
        <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
          <div>
            <Tag size="large" color="blue" style={{ fontSize: 16 }}>
              总分 {detailData.totalScore}
            </Tag>
          </div>
          <div style={{ fontSize: 13, color: "#555", lineHeight: "28px" }}>
            {detailData.name} | {detailData.applyCity || "-"} | {formatAmountZhCn(detailData.applyAmount)} | 填写 {detailData.fieldCount}/9 字段
          </div>
        </div>

        {/* 各字段评分表格 */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#fafafa" }}>
              <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #eee", width: 80 }}>字段</th>
              <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #eee" }}>当前值</th>
              <th style={{ padding: "6px 8px", textAlign: "center", borderBottom: "1px solid #eee", width: 120 }}>得分</th>
              <th style={{ padding: "6px 8px", textAlign: "center", borderBottom: "1px solid #eee", width: 60 }}>满分</th>
            </tr>
          </thead>
          <tbody>
            {details.map((d) => (
              <tr key={d.fieldCode}>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f5f5f5", fontWeight: 500 }}>
                  {d.fieldName || FIELD_LABELS[d.fieldCode] || d.fieldCode}
                </td>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f5f5f5" }}>
                  {d.empty ? (
                    <span style={{ color: "#ccc" }}>未填写(基准分)</span>
                  ) : (
                    <Tag size="small">{d.value}</Tag>
                  )}
                </td>
                <td style={{
                  padding: "6px 8px", borderBottom: "1px solid #f5f5f5", textAlign: "center",
                  color: d.score > 0 ? "#28a745" : d.score < 0 ? "#ff4d4f" : "#999",
                  fontWeight: 600,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                    <Progress
                      percent={d.weight > 0 ? Math.max(0, Math.round(d.score / d.weight * 100)) : 0}
                      size="small"
                      style={{ width: 60 }}
                      stroke={d.score >= d.baseScore ? "#28a745" : "#ff4d4f"}
                      showInfo={false}
                    />
                    {d.score}
                  </div>
                </td>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid #f5f5f5", textAlign: "center", color: "#999" }}>
                  {d.weight}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    );
  };

  return (
    <div style={{ padding: 16 }}>
      {/* 筛选栏 */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Space>
          <Button size="small" type={minScore === 0 ? "primary" : "tertiary"} onClick={() => { setMinScore(0); setPage(1); }}>全部</Button>
          <Button size="small" type={minScore === 60 ? "primary" : "tertiary"} onClick={() => { setMinScore(60); setPage(1); }} style={{ color: minScore === 60 ? "#fff" : "#28a745" }}>高分(60+)</Button>
          <Button size="small" type={minScore === 40 ? "primary" : "tertiary"} onClick={() => { setMinScore(40); setPage(1); }}>中等(40+)</Button>
        </Space>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#666" }}>最低分:</span>
          <Slider value={minScore} min={0} max={100} step={5} style={{ width: 100 }} onChange={(v) => setMinScore(v as number)} onAfterChange={() => setPage(1)} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{minScore}</span>
        </div>
        <Input placeholder="城市筛选" value={city} onChange={setCity} style={{ width: 110 }} onEnterPress={() => { setPage(1); loadData(); }} />
        <Button theme="solid" onClick={() => { setPage(1); loadData(); }}>查询</Button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: "#999" }}>已选 {selectedRows.length} 项</span>
        <Button size="small" loading={scoringLoading} disabled={selectedRows.length === 0} onClick={handleBatchScore}>
          批量评分
        </Button>
        <Button
          size="small"
          onClick={() => {
            // const w = window as any;
            // if (w.navigate) w.navigate("leadExactScoringConfig");
            setShowConfigModal(true);
          }}
        >
          评分配置
        </Button>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="leadId"
        pagination={{
          currentPage: page,
          pageSize,
          total,
          onPageChange: (p) => setPage(p),
        }}
        rowSelection={{
          onChange: (_, rows) => setSelectedRows(rows as LeadExactScoringResult[]),
        }}
        size="small"
        bordered
      />

      {/* 详情弹窗 */}
      {renderDetailModal()}

      {/* 评分规则配置弹窗 */}
            <Modal
              title="评分规则配置"
              visible={showConfigModal}
              onCancel={() => setShowConfigModal(false)}
              footer={null}
              width={1100}
              style={{ top: 20 }}
            >
              <LeadExactScoringConfig />
            </Modal>
    </div>
  );
}
